import { useCallback, useEffect, useMemo, useState } from 'react';

import { Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import type { TFunction } from 'i18next';
import { get } from 'lodash';

import { DEFAULT_MAAS_OPTIONS, MaasModelSourceType, type MaasOptions } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { getActiveModelSourceType, getModelSourceSummary } from '@/delivery-type-plugin/capabilities/maas-init/utils/display';
import { DISABLED_MODEL_SOURCE_TYPES } from '@/delivery-type-plugin/capabilities/maas-model-storage/constants';
import { fetchAndApplyModelConfigResource } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelConfigResource';
import { getModelSourceType } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
import { MODEL_SOURCE_API_MAP } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { parseStorageQuantityToGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/quantity';
import { formatMaasModelSourceApiLabel } from '@/delivery-type-plugin/capabilities/maas-node-groups/labels';

import { isModelSourceComplete, isModelSourcePrefilled } from './model-source-form-rules';

const MODEL_SOURCE_API_OPTIONS = ['HTTP', 'S3', 'ModelScope', 'HuggingFace', 'StackScope', 'ModelPlaza'] as const;

function resolveModelSourceApiOptions(typeOptions: MaasOptions): string[] {
  const fromApi = typeOptions.model?.source || [];
  const merged = [...fromApi];
  MODEL_SOURCE_API_OPTIONS.forEach((source) => {
    if (!merged.includes(source)) merged.push(source);
  });
  return merged;
}

interface UseModelSourceSettingOptions {
  form: FormInstance;
  typeOptions?: MaasOptions;
  t: TFunction;
  /** 多源添加：锁定与首条模型相同的来源类型 */
  lockedSourceType?: MaasModelSourceType;
}

/** 模型来源状态编排：统一处理来源切换、预填补偿、副作用与弹窗行为。 */
export function useModelSourceSetting({ form, typeOptions = DEFAULT_MAAS_OPTIONS, t, lockedSourceType }: UseModelSourceSettingOptions) {
  const watchedSource = Form.useWatch('model.source', form) as Record<string, unknown> | undefined;
  const modelName = Form.useWatch('model.name', form) as string | undefined;
  const modelSize = Form.useWatch('model.size', form) as string | undefined;
  const huggingFaceModel = Form.useWatch('model.source.huggingface.model', form) as string | undefined;
  const modelscopeModel = Form.useWatch('model.source.modelscope.model', form) as string | undefined;

  const [modalVisible, setModalVisible] = useState(false);
  const [modalSession, setModalSession] = useState(0);
  const [sourceType, setSourceType] = useState<MaasModelSourceType>(MaasModelSourceType.ModelScope);

  const activeSourceType = useMemo(() => getActiveModelSourceType(watchedSource as any), [watchedSource]);
  const effectiveSourceType = activeSourceType && !DISABLED_MODEL_SOURCE_TYPES.has(activeSourceType) ? activeSourceType : undefined;
  const isPrefilled = isModelSourcePrefilled(watchedSource, effectiveSourceType);
  const isConfigured = isModelSourceComplete(watchedSource, effectiveSourceType, modelSize) || isPrefilled;

  useEffect(() => {
    if (lockedSourceType) {
      setSourceType(lockedSourceType);
      return;
    }
    const type = getModelSourceType(watchedSource);
    if (type && !DISABLED_MODEL_SOURCE_TYPES.has(type)) setSourceType(type);
  }, [lockedSourceType, watchedSource]);

  useEffect(() => {
    if (modalVisible) return;
    const active = activeSourceType;
    if (active !== MaasModelSourceType.ModelScope && active !== MaasModelSourceType.HuggingFace) return;
    const modelId =
      active === MaasModelSourceType.ModelScope ? String(modelscopeModel || '').trim() : String(huggingFaceModel || '').trim();
    if (!modelId) return;
    fetchAndApplyModelConfigResource(form, active, modelId).catch(() => undefined);
  }, [activeSourceType, form, huggingFaceModel, modelscopeModel, modalVisible]);

  const sourceOptions = useMemo(() => {
    const options = resolveModelSourceApiOptions(typeOptions)
      .map((s) => ({
        label: formatMaasModelSourceApiLabel(s, t),
        value: MODEL_SOURCE_API_MAP[s] || s.toLowerCase(),
      }))
      .filter((option) => !DISABLED_MODEL_SOURCE_TYPES.has(option.value as MaasModelSourceType));
    if (!lockedSourceType) return options;
    return options.filter((option) => option.value === lockedSourceType);
  }, [lockedSourceType, typeOptions, t]);

  const handleSourceTypeChange = useCallback(
    (type: MaasModelSourceType) => {
      if (lockedSourceType && type !== lockedSourceType) return;
      setSourceType(type);
      form.setFieldValue('model.size', undefined);
      form.setFieldValue('model.source', {
        modelscope: undefined,
        huggingface: undefined,
        stackscope: undefined,
        modelplaza: undefined,
        [type]: {},
      });
    },
    [form, lockedSourceType],
  );

  const openModal = useCallback(() => {
    const type = lockedSourceType ?? getModelSourceType(watchedSource);
    const nextType = type && !DISABLED_MODEL_SOURCE_TYPES.has(type) ? type : MaasModelSourceType.ModelScope;
    setSourceType(nextType);
    setModalSession((session) => session + 1);
    setModalVisible(true);
  }, [lockedSourceType, watchedSource]);

  const closeModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  const sourceSummary = useMemo(() => getModelSourceSummary(watchedSource as any, t, modelName), [modelName, t, watchedSource]);
  const sourceDetail = useMemo(() => {
    if (!activeSourceType || !watchedSource) return '';
    const block = watchedSource[activeSourceType];
    if (!block || typeof block !== 'object') return '';
    return String(get(block, 'url') || get(block, 'model') || '').trim();
  }, [activeSourceType, watchedSource]);
  const modelSizeLabel = useMemo(() => {
    if (!modelSize) return undefined;
    const gi = parseStorageQuantityToGi(modelSize);
    if (gi !== undefined && gi > 0) return `${gi}Gi`;
    return modelSize;
  }, [modelSize]);

  return {
    modalVisible,
    modalSession,
    sourceType,
    sourceOptions,
    isConfigured,
    modelName,
    sourceSummary,
    sourceDetail,
    modelSizeLabel,
    handleSourceTypeChange,
    openModal,
    closeModal,
    setModalVisible,
    sourceTypeLocked: !!lockedSourceType,
  };
}
